'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _extends = require('@babel/runtime/helpers/extends');
var React = require('react');
var mustache = require('mustache');
var nthHookSettings = require('@narvar/nth-hook-settings');
var nthHookContextPage = require('@narvar/nth-hook-context-page');
var useOrderTracking = require('@narvar/nth-hook-order-tracking');
var nthHookContextAnalytics = require('@narvar/nth-hook-context-analytics');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthBitHeader = require('@narvar/nth-bit-header');
var TextLink = require('@narvar/nth-bit-text-link');
var Alert = require('@narvar/nth-block-alert');
var PrefetchData = require('./PrefetchData.js');
var BroadcastBanner_module = require('./BroadcastBanner.module.css.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var _extends__default = /*#__PURE__*/_interopDefaultLegacy(_extends);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var mustache__default = /*#__PURE__*/_interopDefaultLegacy(mustache);
var useOrderTracking__default = /*#__PURE__*/_interopDefaultLegacy(useOrderTracking);
var TextLink__default = /*#__PURE__*/_interopDefaultLegacy(TextLink);
var Alert__default = /*#__PURE__*/_interopDefaultLegacy(Alert);

const localStorage = (() => {
  if (typeof window === 'undefined') return undefined;
  try {
    // reading the localStorage property can throw if we're in an iframe
    // see: https://stackoverflow.com/questions/67645164
    return window.localStorage;
  } catch (err) {
    return undefined;
  }
})();
const BlockBroadcastBanner = ({
  featureName
}) => {
  const {
    state: pageContext,
    state: {
      retailerMoniker
    }
  } = nthHookContextPage.usePageContext();
  const {
    broadcastBanner
  } = nthHookSettings.useSettings();
  const {
    uid,
    startDateUtc: startDate,
    endDateUtc: endDate,
    title: messageTitle,
    body: messageBody,
    linkText,
    linkUrl,
    dismissible: dismissibleIn
  } = broadcastBanner || {};
  const dismissible = dismissibleIn === null || dismissibleIn === undefined || dismissibleIn;
  const {
    linksTrackingDefault: linkStyle,
    linksTrackingHover: linkHoverStyle,
    bodytextDefaultNone
  } = nthHookTheme.useTheme();
  const {
    getCurrentShipment
  } = useOrderTracking__default["default"]();
  const {
    trackingNumber = '',
    trackingUrl = ''
  } = getCurrentShipment() ?? {};
  const localStorageKey = uid ? `broadcastBanner_${uid}` : `broadcastBanner_${retailerMoniker}_${startDate ?? ''}${endDate ?? ''}`;
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    if (localStorage) {
      try {
        const broadcastBannerStorage = localStorage.getItem(localStorageKey);
        setVisible(!broadcastBannerStorage);
      } catch (err) {
        setVisible(true);
      }
    }
  },
  // TODO: Make this dependency array exhaustive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const logEvent = nthHookContextAnalytics.useAnalyticsPost();
  const baseLogInfo = {
    eventComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_BLOCK_BROADCAST_BANNER,
    eventFeature: nthHookContextAnalytics.NarvarEventComponents.NTH_BLOCK_BROADCAST_BANNER,
    feature: featureName,
    pageComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_BLOCK_BROADCAST_BANNER
  };
  React.useEffect(() => {
    if (!messageBody && !messageTitle && !linkText) return;
    ({
      ...baseLogInfo,
      clickAssetId: uid,
      clickAssetSchdEndDateUtc: endDate,
      clickAssetSchdStartDateUtc: startDate,
      eventType: nthHookContextAnalytics.NarvarEventTypes.COMPONENT_RENDER
    });
  },
  // TODO: Make this dependency array exhaustive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [messageTitle, messageBody, linkText]);
  if (!messageBody && !messageTitle && !linkText) return null;
  const now = new Date();
  // date is null means "Start it Right Away" for start date and "Never Ending" for end date
  if (startDate && new Date(startDate) >= now) return null;
  if (endDate && new Date(endDate) <= now) return null;
  const bannerMessageTemplateOptions = {
    ...pageContext,
    trackingUrl,
    trackingNumber
  };
  const messageBodyRendered = mustache__default["default"].render(messageBody || '', bannerMessageTemplateOptions);
  const onDismissClicked = () => {
    if (localStorage) {
      try {
        localStorage.setItem(localStorageKey, 'true');
        // eslint-disable-next-line no-empty
      } catch (err) {}
    }
    setVisible(false);
    logEvent({
      ...baseLogInfo,
      clickAssetId: uid,
      clickCategory: nthHookContextAnalytics.NarvarClickSubTypes.DISMISS,
      clickSubType: nthHookContextAnalytics.NarvarClickSubTypes.DISMISS,
      clickType: nthHookContextAnalytics.NarvarClickTypes.INTERACTION,
      eventType: nthHookContextAnalytics.NarvarEventTypes.CLICK
    });
  };
  return /*#__PURE__*/React__default["default"].createElement(Alert__default["default"], {
    featureName: featureName,
    show: visible,
    dismissible: dismissible,
    dismissOnClick: onDismissClicked,
    "data-test": "broadcastBannerFeature"
  }, messageTitle && /*#__PURE__*/React__default["default"].createElement(nthBitHeader.MediumHeader, {
    as: "h2",
    featureName: featureName
  }, messageTitle), messageBody && /*#__PURE__*/React__default["default"].createElement("div", {
    className: BroadcastBanner_module["default"].bannerText,
    style: bodytextDefaultNone
    // eslint-disable-next-line react/no-danger
    ,
    dangerouslySetInnerHTML: {
      __html: messageBodyRendered
    }
  }), linkText && /*#__PURE__*/React__default["default"].createElement(TextLink__default["default"], {
    target: TextLink.TextLinkTargetType.BLANK,
    linkStyle: {
      sDefault: linkStyle,
      sHover: linkHoverStyle
    },
    href: linkUrl || '',
    onClick: () => logEvent({
      ...baseLogInfo,
      clickAssetId: uid,
      clickCategory: nthHookContextAnalytics.NarvarClickSubTypes.EXTERNAL_LINK,
      clickLinkDestinationUrl: linkUrl,
      clickLinkLabel: linkText,
      clickSubType: nthHookContextAnalytics.NarvarClickSubTypes.EXTERNAL_LINK,
      clickType: nthHookContextAnalytics.NarvarClickTypes.OUTGOING,
      eventType: nthHookContextAnalytics.NarvarEventTypes.CLICK
    })
  }, linkText));
};
const BlockBroadcastBannerWithData = nthHookSettings.withSettingsProvider(nthHookTheme.withThemeProvider(BlockBroadcastBanner, {
  themeTokens: ['linksTrackingDefault', 'linksTrackingHover', 'bodytextDefaultNone']
}), {
  settingsRequested: `
      broadcastBanner {
        uid
        startDateUtc
        endDateUtc
        title
        body
        linkText
        linkUrl
        dismissible
      }
    `
});
const BlockBroadcastBannerWithPrefetchedData = ({
  featureName,
  ...props
}) => /*#__PURE__*/React__default["default"].createElement(PrefetchData["default"], {
  featureName: featureName
}, /*#__PURE__*/React__default["default"].createElement(BlockBroadcastBannerWithData, _extends__default["default"]({
  featureName: featureName
}, props)));
BlockBroadcastBannerWithPrefetchedData.propTypes = BlockBroadcastBannerWithData.propTypes;

exports["default"] = BlockBroadcastBannerWithPrefetchedData;
